import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import { DropdownButtonSize } from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownDivider from '@/storybook/components/FormCollection/components/DropdownDivider/DropdownDivider';
import ListHeader from '@/storybook/components/FormCollection/components/ListHeader/ListHeader';
import useVirtualizedOptions, {
  VirtualRowMeta,
} from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import { getCascadingItemBadge } from '@/storybook/components/FormCollection/utils/cascadingItemBadge';
import {
  createSelectItemKey,
  extractSelectItemLabelText,
  getPointerModifiersFromSyntheticEvent,
  getSelectItemLabelContent,
  type PointerModifierState,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { memo, useCallback, type ChangeEvent, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../Input.module.css';

type MultiSelectOptionClickHandler<T> = (
  selectedValue: T,
  scopedOptionsValue: T[],
  modifiers: PointerModifierState,
) => void;

type MultiSelectVirtualizedOptionRowProps<T> = {
  option: SelectItem<T>;
  isSelectedRow: boolean;
  optionValues: T[];
  handleOptionClick: MultiSelectOptionClickHandler<T>;
  isOptionSelected: (option: SelectItem<T>) => boolean;
  disabledTooltip: string;
  size: keyof typeof DropdownButtonSize;
};

function MultiSelectVirtualizedOptionRowInner<T>({
  option,
  isSelectedRow,
  optionValues,
  handleOptionClick,
  isOptionSelected,
  disabledTooltip,
  size,
}: MultiSelectVirtualizedOptionRowProps<T>) {
  const labelText = extractSelectItemLabelText(option);
  const labelContent = getSelectItemLabelContent(option);
  const optionKey = createSelectItemKey(option);
  const rowPrefix = isSelectedRow ? 'sortedSelectedOption' : 'sortedNotSelectedOption';
  const isDisabled = option.disabled === true;

  const handleRowClick = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      handleOptionClick(option.value, optionValues, getPointerModifiersFromSyntheticEvent(e));
    },
    [handleOptionClick, option.value, optionValues],
  );

  const handleCheck = useCallback(
    (e?: ChangeEvent<HTMLInputElement>) => {
      handleOptionClick(option.value, optionValues, getPointerModifiersFromSyntheticEvent(e));
    },
    [handleOptionClick, option.value, optionValues],
  );

  const checked = isOptionSelected(option);
  const badge = getCascadingItemBadge(option, checked);

  return (
    <TreeItem
      className={!isSelectedRow ? styles.noSelect : undefined}
      label={labelText}
      labelContent={labelContent}
      tooltipText={isDisabled ? disabledTooltip : undefined}
      forceTooltip={isDisabled && checked}
      variant="flatList"
      level={0}
      showCheckbox
      checked={checked}
      onCheck={isDisabled ? undefined : handleCheck}
      onClick={isDisabled ? undefined : handleRowClick}
      isSelected={checked}
      isDisabled={isDisabled}
      badge={badge}
      data-testid={`${rowPrefix}Row-${optionKey}`}
      checkboxDataTestId={`${rowPrefix}-${optionKey}`}
      size={size}
    />
  );
}

// Preserve generic type through React.memo via type assertion
const MultiSelectVirtualizedOptionRow = memo(
  MultiSelectVirtualizedOptionRowInner,
) as typeof MultiSelectVirtualizedOptionRowInner;

type UseMultiSelectVirtualizationParams<T> = {
  dedupedSelectedOptions: SelectItem<T>[];
  notSelectedOptions: SelectItem<T>[];
  selectedOptionValues: T[];
  notSelectedOptionValues: T[];
  categoriesToDisplay: Array<{ category: string; index: number }>;
  hasCategorizedOptions: boolean;
  needsDivider: boolean;
  noCategoryLabel: string;
  handleClick: MultiSelectOptionClickHandler<T>;
  isOptionSelected: (option: SelectItem<T>) => boolean;
  size?: keyof typeof DropdownButtonSize;
};

export function useMultiSelectVirtualization<T>({
  dedupedSelectedOptions,
  notSelectedOptions,
  selectedOptionValues,
  notSelectedOptionValues,
  categoriesToDisplay,
  hasCategorizedOptions,
  needsDivider,
  noCategoryLabel,
  handleClick,
  isOptionSelected,
  size = 'L',
}: UseMultiSelectVirtualizationParams<T>) {
  const { t } = useTranslation();
  const disabledTooltip = t('components.dropdownPanel.valueNoLongerApplicable');

  const { virtualizedList, parentRef } = useVirtualizedOptions<T>({
    selectedOptions: dedupedSelectedOptions,
    notSelectedOptions,
    hasCategorizedOptions,
    categoriesToDisplay,
    includeDivider: needsDivider,
    noCategoryLabel,
  });

  const renderVirtualRow = useCallback(
    (vMeta: VirtualRowMeta<T>, virtualIndex?: number) => {
      if (vMeta.kind === 'divider') {
        return <DropdownDivider key={`divider-${virtualIndex}`} />;
      }
      if (vMeta.kind === 'category' && vMeta.category) {
        return (
          <ListHeader
            key={`category-${vMeta.category}`}
            category={vMeta.category}
            isNoCategory={vMeta.isNoCategory}
            hideDivider={vMeta.isFirstCategory}
          />
        );
      }
      if (vMeta.option) {
        const { option } = vMeta;
        if (option == null) return null;
        const isSelectedRow = vMeta.kind === 'selected';
        const optionValues = isSelectedRow ? selectedOptionValues : notSelectedOptionValues;
        return (
          <MultiSelectVirtualizedOptionRow<T>
            key={createSelectItemKey(option)}
            option={option}
            isSelectedRow={isSelectedRow}
            size={size}
            optionValues={optionValues}
            handleOptionClick={handleClick}
            isOptionSelected={isOptionSelected}
            disabledTooltip={disabledTooltip}
          />
        );
      }
      return null;
    },
    [handleClick, selectedOptionValues, size, notSelectedOptionValues, isOptionSelected, disabledTooltip],
  );

  return { virtualizedList, parentRef, renderVirtualRow };
}
